import { useCallback, useEffect, useMemo, useState } from "react";
import { callFunction } from "../api/callFunction";
import { BellAlertIcon } from "@heroicons/react/24/outline";
import { PrimaryButtonSmall, SecondaryButtonSmall } from "../components/Button";
import { showToast } from "../utils/showToast";
import { timeAgo } from "../Events/outreachFormat";
import { NudgePreviewDialog, HostNudgeHistoryDialog } from "./HostNudgeDialogs";
import { statusChip, describeConfig, modeLabel, shortDate, resultLine } from "./hostNudgeFormat";

// Host nudges: the text that asks a host to plan their next events.
//
// A daily sweep picks the hosts who are due, drafts a few ideas from what they
// have run before and what is open in their area's idea feed, and texts them a
// link to the panel. This page is the whole control surface: the settings, who
// is due and why, a per-host switch, and a preview or a manual send.
//
// It ships off. Until someone turns it on here, nothing is ever sent.

const KNOBS = [
  { key: "cadenceDays", label: "Days between texts", help: "A host is never texted more often than this." },
  { key: "runwayDays", label: "Skip when booked this far ahead", help: "Days. A host with an event further out than this is left alone." },
  { key: "maxUnanswered", label: "Stop after this many with no reply", help: "Texts in a row that led to no new event." },
  { key: "horizonDays", label: "Plan this many days ahead", help: "How far out the drafted ideas reach." },
  { key: "dailyCap", label: "Most texts per day", help: "A ceiling on one day's run." },
  { key: "sendHour", label: "Send hour, Pacific", help: "0 to 23. The daily run acts only in this hour." },
];

const call = (name, payload, timeout = 60000) => callFunction(name, payload, { timeout });

const NumberField = ({ id, label, help, value, min, max, onChange, disabled }) => (
  <div>
    <label htmlFor={id} className="block text-xs font-medium text-gray-700 dark:text-gray-300">{label}</label>
    <input
      id={id}
      type="number"
      inputMode="numeric"
      min={min}
      max={max}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1.5 text-sm text-gray-900 dark:text-gray-100"
    />
    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{help}</p>
  </div>
);

export default function HostNudges() {
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState(null);
  const [history, setHistory] = useState([]);
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState("");
  const [preview, setPreview] = useState(null); // { row, data }
  const [historyFor, setHistoryFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cfgData, listData] = await Promise.all([call("adminGetHostCadenceConfig", {}), call("adminListHostCadence", {})]);
      const cfg = cfgData || {};
      setMeta(cfg);
      setForm({ ...cfg.config });
      setHistory(Array.isArray(cfg.history) ? cfg.history : []);
      setRows(Array.isArray(listData?.rows) ? listData.rows : []);
    } catch (err) {
      console.error("Host nudges failed to load", err);
      showToast(err?.message || "Could not load host nudges.", { type: "error" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const enabled = Boolean(meta?.config?.enabled);
  const limits = meta?.limits || {};
  const dueCount = useMemo(() => rows.filter((r) => r.evaluation?.due).length, [rows]);

  const handleSave = async () => {
    if (!form) return;
    const payload = { enabled: form.enabled === true, expectedVersion: meta?.version ?? null };
    for (const { key, label } of KNOBS) {
      const n = Number(form[key]);
      const [min, max] = limits[key] || [];
      if (!Number.isInteger(n) || (min != null && n < min) || (max != null && n > max)) {
        showToast(`${label} must be a whole number from ${min} to ${max}.`, { type: "error" });
        return;
      }
      payload[key] = n;
    }
    if (!window.confirm(`Apply these nudge settings?\n\n${describeConfig({ ...form, ...payload })}`)) return;
    setSaving(true);
    try {
      await call("adminUpdateHostCadenceConfig", payload);
      showToast("Settings saved.");
      await load();
    } catch (err) {
      console.error("adminUpdateHostCadenceConfig failed", err);
      showToast(err?.message || "Could not save the settings.", { type: "error" });
      if (err?.code === "functions/failed-precondition") await load();
    } finally {
      setSaving(false);
    }
  };

  const patchRow = (vendorID, patch) => {
    setRows((prev) => prev.map((r) => (r.vendorID === vendorID ? { ...r, ...patch } : r)));
  };

  const handleMode = async (row, adminMode) => {
    const before = row.adminMode;
    patchRow(row.vendorID, { adminMode }); // optimistic
    try {
      await call("adminSetHostCadence", { vendorID: row.vendorID, adminMode });
      await load();
    } catch (err) {
      console.error("adminSetHostCadence failed", err);
      patchRow(row.vendorID, { adminMode: before });
      showToast(err?.message || "Could not change this host.", { type: "error" });
    }
  };

  const handleResume = async (row) => {
    setBusyId(row.vendorID);
    try {
      await call("adminSetHostCadence", { vendorID: row.vendorID, resetUnanswered: true });
      showToast(`${row.name || "This host"} is back in the cadence.`);
      await load();
    } catch (err) {
      console.error("adminSetHostCadence failed", err);
      showToast(err?.message || "Could not restart this host.", { type: "error" });
    } finally {
      setBusyId("");
    }
  };

  const handlePreview = async (row) => {
    setBusyId(row.vendorID);
    try {
      const data = await call("adminSendHostCadenceNudge", { vendorID: row.vendorID, dryRun: true }, 190000);
      setPreview({ row, data });
    } catch (err) {
      console.error("adminSendHostCadenceNudge preview failed", err);
      showToast(err?.message || "Could not draft their nudge.", { type: "error" });
    } finally {
      setBusyId("");
    }
  };

  const handleSend = async (row) => {
    if (!window.confirm(`Text ${row.name || "this host"} now? This ignores the cadence and the booked-ahead rule.`)) return;
    setBusyId(row.vendorID);
    try {
      const data = await call("adminSendHostCadenceNudge", { vendorID: row.vendorID }, 190000);
      if (data?.sent) showToast(`Texted ${row.name || "them"}.`);
      else showToast(data?.error || "The text did not go out.", { type: "error" });
      setPreview(null);
      await load();
    } catch (err) {
      console.error("adminSendHostCadenceNudge failed", err);
      showToast(err?.message || "Could not send the text.", { type: "error" });
    } finally {
      setBusyId("");
    }
  };

  if (loading || !form) {
    return <div className="p-4 text-sm text-gray-500 dark:text-gray-400">Loading host nudges…</div>;
  }

  return (
    <div className="p-4 space-y-6">
      <div className="flex items-start gap-3">
        <BellAlertIcon className="w-6 h-6 text-gray-400 shrink-0 mt-0.5" aria-hidden="true" />
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Host nudges</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            A text that asks a host to plan their next events, with ideas drafted from what they have run before.
          </p>
        </div>
      </div>

      {!enabled ? (
        <div className="rounded-md border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/30 p-3 text-sm text-amber-900 dark:text-amber-200">
          Nudges are off. Nothing is being sent. You can still preview a host's text, or send one by hand.
        </div>
      ) : null}

      <section className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="font-medium text-gray-900 dark:text-gray-100">Settings</h2>
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={form.enabled === true}
              onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
              className="rounded border-gray-300 dark:border-gray-600"
            />
            Send these texts
          </label>
        </div>

        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {KNOBS.map(({ key, label, help }) => (
            <NumberField
              key={key}
              id={`host-nudge-${key}`}
              label={label}
              help={help}
              value={form[key]}
              min={limits[key]?.[0]}
              max={limits[key]?.[1]}
              disabled={saving}
              onChange={(v) => setForm((f) => ({ ...f, [key]: v === "" ? "" : Number(v) }))}
            />
          ))}
        </div>

        <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {meta?.isDefault
              ? "Nothing saved yet, so these are the defaults."
              : `Version ${meta?.version} · Last changed ${meta?.updatedAtMs ? new Date(meta.updatedAtMs).toLocaleString() : "recently"}${meta?.updatedByName ? ` by ${meta.updatedByName}` : ""}`}
          </p>
          <PrimaryButtonSmall onClick={handleSave} loading={saving} loadingLabel="Saving" disabled={saving}>
            Save settings
          </PrimaryButtonSmall>
        </div>

        {history.length > 0 ? (
          <div className="mt-4 border-t border-gray-200 dark:border-gray-700 pt-3">
            <div className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Recent changes</div>
            <ul className="mt-2 space-y-1 text-xs text-gray-600 dark:text-gray-300">
              {history.slice(0, 5).map((h) => (
                <li key={h.id}>
                  <span className="text-gray-400 dark:text-gray-500">
                    {h.updatedAtMs ? new Date(h.updatedAtMs).toLocaleDateString() : ""}
                    {h.updatedByName ? ` · ${h.updatedByName}` : ""}
                  </span>{" "}
                  {describeConfig(h.after)}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </section>

      <section>
        <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
          <h2 className="font-medium text-gray-900 dark:text-gray-100">
            Hosts <span className="text-sm font-normal text-gray-500 dark:text-gray-400">({rows.length}, {dueCount} due)</span>
          </h2>
          <SecondaryButtonSmall onClick={load}>Reload</SecondaryButtonSmall>
        </div>

        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                <th className="px-3 py-2">Host</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Calendar</th>
                <th className="px-3 py-2">Nudges</th>
                <th className="px-3 py-2">Texts</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-900">
              {rows.map((row) => {
                const chip = statusChip(row, enabled);
                const busy = busyId === row.vendorID;
                return (
                  <tr key={row.vendorID} className="align-top">
                    <td className="px-3 py-3">
                      <div className="font-medium text-gray-900 dark:text-gray-100">{row.name || "Unnamed host"}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {row.eventCount == null ? "" : `${row.eventCount} event${row.eventCount === 1 ? "" : "s"}`}
                        {row.isSuperAdmin ? " · admin" : ""}
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${chip.className}`}>{chip.label}</span>
                      {row.lastSendError ? (
                        <div className="mt-1 text-xs text-red-600 dark:text-red-400">{row.lastSendError}</div>
                      ) : null}
                    </td>
                    <td className="px-3 py-3 text-xs text-gray-600 dark:text-gray-300">
                      {row.upcomingCount > 0
                        ? `${row.upcomingCount} coming up, last on ${shortDate(row.calendarEndsAt)}`
                        : "Nothing coming up"}
                    </td>
                    <td className="px-3 py-3 text-xs text-gray-600 dark:text-gray-300">
                      <div>{resultLine(row)}</div>
                      {row.lastNudgedAt ? (
                        <div className="text-gray-400 dark:text-gray-500">Last {timeAgo(row.lastNudgedAt)}</div>
                      ) : null}
                      {row.planSummary ? (
                        <div className="text-gray-400 dark:text-gray-500">
                          {row.planSummary.ideaCount} idea{row.planSummary.ideaCount === 1 ? "" : "s"} drafted
                        </div>
                      ) : null}
                    </td>
                    <td className="px-3 py-3">
                      <label className="sr-only" htmlFor={`mode-${row.vendorID}`}>Nudges for this host</label>
                      <select
                        id={`mode-${row.vendorID}`}
                        value={row.adminMode}
                        onChange={(e) => handleMode(row, e.target.value)}
                        className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1 text-xs text-gray-900 dark:text-gray-100"
                      >
                        <option value="inherit">{modeLabel("inherit")}</option>
                        <option value="on">{modeLabel("on")}</option>
                        <option value="off">{modeLabel("off")}</option>
                      </select>
                      {row.hostOptOut ? (
                        <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">They turned these off</div>
                      ) : null}
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex flex-wrap gap-2 justify-end">
                        <SecondaryButtonSmall onClick={() => handlePreview(row)} disabled={busy}>Preview</SecondaryButtonSmall>
                        <SecondaryButtonSmall onClick={() => handleSend(row)} disabled={busy || !row.hasPhone}>Send now</SecondaryButtonSmall>
                        {row.evaluation?.reason === "unanswered_cap" ? (
                          <SecondaryButtonSmall onClick={() => handleResume(row)} disabled={busy}>Resume</SecondaryButtonSmall>
                        ) : null}
                        <SecondaryButtonSmall onClick={() => setHistoryFor(row)} disabled={busy}>History</SecondaryButtonSmall>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 ? (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-sm text-gray-500 dark:text-gray-400">No hosts yet.</td></tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>

      {preview ? (
        <NudgePreviewDialog
          row={preview.row}
          preview={preview.data}
          sending={busyId === preview.row.vendorID}
          onSend={() => handleSend(preview.row)}
          onClose={() => setPreview(null)}
        />
      ) : null}
      {historyFor ? <HostNudgeHistoryDialog row={historyFor} onClose={() => setHistoryFor(null)} /> : null}
    </div>
  );
}
